import { Badge, Box, Flex, Stack, Text } from '@mantine/core'
import { IconDownload, IconEye, IconFileTypeDocx, IconReplace } from '@tabler/icons-react'
import { useMemo } from 'react'
import { BookData, ChapterData } from 'src/interfaces'
import { downloadWordFile, getChapterWordFile } from 'src/utils/chapterWordFile'
import { openFilePreview } from 'src/utils/fileUrl'

// UD-614: the English Word file of every chapter of a book, with View, Download (the original
// file) and Replace. Shown from the book row (Word file) and inside the book form.

interface BookWordFilesProps {
	book: BookData | null
	onReplace: (chapter: ChapterData) => void
}

const linkButton = {
	display: 'inline-flex',
	alignItems: 'center',
	gap: 4,
	border: '1px solid #cfe4d6',
	borderRadius: 8,
	background: '#fff',
	color: '#006b35',
	fontWeight: 600,
	fontSize: 13,
	padding: '6px 10px',
	cursor: 'pointer',
	whiteSpace: 'nowrap' as const,
}

function BookWordFiles({ book, onReplace }: BookWordFilesProps) {
	const chapters = useMemo(
		() =>
			[...(book?.chapters || [])].sort(
				(a, b) => (a.chapter_number ?? 0) - (b.chapter_number ?? 0),
			),
		[book?.chapters],
	)

	if (!chapters.length) {
		return (
			<Text size="sm" color="dimmed">
				This book has no chapters yet, so there is no Word file. Add a chapter to upload one.
			</Text>
		)
	}

	return (
		<Stack spacing={8}>
			{chapters.map((chapter, index) => {
				const file = getChapterWordFile(chapter, book)
				const number = chapter.chapter_number ?? index + 1
				const published =
					String(chapter.published_status || '').toLowerCase() === 'published'

				return (
					<Box
						key={chapter.id}
						data-testid={`word-file-row-${chapter.id}`}
						sx={{
							border: '1px solid #e6ebe7',
							borderRadius: 10,
							padding: 12,
							background: '#fff',
						}}
					>
						<Flex justify="space-between" align="center" gap={12} wrap="wrap">
							<Stack spacing={4} sx={{ minWidth: 0, flex: '1 1 260px' }}>
								<Flex align="center" gap={8} wrap="wrap">
									<Text size="xs" weight={700} sx={{ color: '#6b7280' }}>
										CHAPTER {number}
									</Text>
									<Text size="xs" color="dimmed" lineClamp={1}>
										{chapter.title || '-'}
									</Text>
									<Badge size="xs" variant="light" color={published ? 'green' : 'gray'}>
										{published ? 'Published' : 'Not published'}
									</Badge>
								</Flex>
								{file.url ? (
									<Flex align="center" gap={6} sx={{ minWidth: 0 }}>
										<IconFileTypeDocx size={18} color="#1f5fbf" style={{ flexShrink: 0 }} />
										<Text size="sm" weight={600} lineClamp={1} title={file.name}>
											{file.name}
										</Text>
									</Flex>
								) : (
									<Text size="sm" color="dimmed">
										No English Word file
									</Text>
								)}
								{file.url && !file.nameRecorded && (
									<Text size="xs" color="dimmed">
										Uploaded before file names were kept
									</Text>
								)}
							</Stack>

							<Flex gap={6} wrap="wrap">
								{file.url && (
									<>
										<button
											type="button"
											style={linkButton}
											onClick={() => openFilePreview(file.url)}
										>
											<IconEye size={15} />
											View
										</button>
										<button
											type="button"
											style={linkButton}
											onClick={() => void downloadWordFile(file.url, file.name)}
										>
											<IconDownload size={15} />
											Download
										</button>
									</>
								)}
								<button
									type="button"
									style={linkButton}
									onClick={() => onReplace(chapter)}
								>
									<IconReplace size={15} />
									Replace
								</button>
							</Flex>
						</Flex>
					</Box>
				)
			})}
		</Stack>
	)
}

export default BookWordFiles
